import { VesselInsightSignals } from './vessel-insights-normalize.util';

export interface BenchmarkComparisonView {
  actualMt: number | null;
  tcpMt: number | null;
  baselineMt: number | null;
  tcpVarianceMt: number | null;
  tcpVariancePct: number | null;
  baselineVarianceMt: number | null;
  baselineVariancePct: number | null;
  hasData: boolean;
}

export interface ScoreBreakdownItem {
  label: string;
  impact: number;
}

export interface ExtractionQualityMeta {
  pageCount: number;
  pagesIncluded: number[];
  textChars: number;
  usedOcr: boolean;
  markerCount: number;
}

export interface InsightTagView {
  label: string;
  variant: 'orange' | 'blue' | 'purple' | 'yellow';
}

const PHASE_PATTERNS = [
  /idle\s*(?:period|time|duration)?/i,
  /cargo\s*(?:operations?|ops)/i,
  /manoeuvr(?:ing)?|maneuver(?:ing)?/i,
];

function roundMt(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundPct(value: number): number {
  return Math.round(value * 10) / 10;
}

function toNum(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseSignedPct(text: string, patterns: RegExp[]): number | null {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) {
      const value = Number(match[1]);
      if (Number.isFinite(value)) {
        return roundPct(value);
      }
    }
  }
  return null;
}

function parseMtFromPatterns(text: string, patterns: RegExp[]): number | null {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) {
      const value = Number(match[1]);
      if (Number.isFinite(value) && value >= 0) {
        return roundMt(value);
      }
    }
  }
  return null;
}

function parseVarianceDirection(
  text: string,
  benchmark: 'tcp' | 'baseline',
): number | null {
  const label =
    benchmark === 'tcp'
      ? /(?:tcp|charter[\s-]?party|cp)\s*(?:benchmark|consumption)?/i
      : /(?:technical\s+)?(?:baseline|design)\s*(?:benchmark|consumption)?/i;

  const patterns = [
    new RegExp(
      `(\\d+(?:\\.\\d+)?)\\s*%\\s*(below|under|less\\s+than|above|over|exceed(?:ed|s)?)\\s+(?:the\\s+)?${label.source}`,
      'i',
    ),
    new RegExp(
      `(?:was|is)\\s+(\\d+(?:\\.\\d+)?)\\s*%\\s*(below|under|above|over)\\s+(?:the\\s+)?${label.source}`,
      'i',
    ),
    new RegExp(
      `${label.source}[^.\\n]{0,40}?(\\d+(?:\\.\\d+)?)\\s*%\\s*(below|under|above|over)`,
      'i',
    ),
    new RegExp(
      `(below|under|above|over)\\s+(?:the\\s+)?${label.source}\\s+by\\s+(\\d+(?:\\.\\d+)?)\\s*%`,
      'i',
    ),
    new RegExp(
      `(\\d+(?:\\.\\d+)?)\\s*%\\s*(below|under|above|over)\\s+${label.source}`,
      'i',
    ),
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (!match) {
      continue;
    }

    let pct: number;
    let direction: string;
    if (/below|under|above|over/i.test(match[1])) {
      direction = match[1].toLowerCase();
      pct = Number(match[2]);
    } else {
      pct = Number(match[1]);
      direction = (match[2] || '').toLowerCase();
    }

    if (!Number.isFinite(pct)) {
      continue;
    }

    const negative = /below|under|less/.test(direction);
    const positive = /above|over|exceed/.test(direction);
    if (negative) {
      return roundPct(-Math.abs(pct));
    }
    if (positive) {
      return roundPct(Math.abs(pct));
    }
  }

  return null;
}

function sumNumbers(values: Array<number | null>): number | null {
  const nums = values.filter((v): v is number => v != null);
  if (!nums.length) {
    return null;
  }
  return roundMt(nums.reduce((sum, value) => sum + value, 0));
}

function parsePhaseSectionValue(section: string, kind: 'actual' | 'tcp' | 'baseline'): number | null {
  const patterns =
    kind === 'actual'
      ? [
          /actual\s*(?:consumption)?[^0-9]{0,30}(\d+(?:\.\d+)?)/i,
          /actual[^0-9]{0,20}(\d+(?:\.\d+)?)\s*(?:mt)?/i,
        ]
      : kind === 'tcp'
        ? [
            /tcp\s*(?:benchmark|consumption)?[^0-9]{0,40}(\d+(?:\.\d+)?)/i,
            /(?:cp|charter[\s-]?party)\s*(?:benchmark|consumption)?[^0-9]{0,30}(\d+(?:\.\d+)?)/i,
          ]
        : [
            /(?:technical\s+)?(?:baseline|design)\s*(?:benchmark|consumption)?[^0-9]{0,40}(\d+(?:\.\d+)?)/i,
            /baseline[^0-9]{0,25}(\d+(?:\.\d+)?)/i,
          ];

  const direct = parseMtFromPatterns(section, patterns);
  if (direct != null) {
    return direct;
  }

  const fuelNums = [...section.matchAll(/(\d+(?:\.\d+)?)/g)]
    .map((m) => Number(m[1]))
    .filter((n) => Number.isFinite(n) && n > 0 && n < 5000);

  if (fuelNums.length >= 1 && fuelNums.length <= 4) {
    return roundMt(fuelNums.reduce((sum, n) => sum + n, 0));
  }

  return null;
}

function extractPhaseSection(text: string, phasePattern: RegExp): string {
  const match = text.match(
    new RegExp(
      `(${phasePattern.source})[\\s\\S]{0,900}?(?=(?:idle|cargo|manoeuvr|maneuver|total\\s+consumption|benchmark\\s+comparison|$))`,
      'i',
    ),
  );
  return match?.[0] ?? '';
}

function parsePhaseTotals(text: string): {
  actualMt: number | null;
  tcpMt: number | null;
  baselineMt: number | null;
} {
  const actualParts: Array<number | null> = [];
  const tcpParts: Array<number | null> = [];
  const baselineParts: Array<number | null> = [];

  for (const phasePattern of PHASE_PATTERNS) {
    const section = extractPhaseSection(text, phasePattern);
    if (!section) {
      continue;
    }
    actualParts.push(parsePhaseSectionValue(section, 'actual'));
    tcpParts.push(parsePhaseSectionValue(section, 'tcp'));
    baselineParts.push(parsePhaseSectionValue(section, 'baseline'));
  }

  return {
    actualMt: sumNumbers(actualParts),
    tcpMt: sumNumbers(tcpParts),
    baselineMt: sumNumbers(baselineParts),
  };
}

function calcVariancePct(actual: number | null, benchmark: number | null): number | null {
  if (actual == null || benchmark == null || benchmark === 0) {
    return null;
  }
  return roundPct(((actual - benchmark) / benchmark) * 100);
}

function calcVarianceMt(actual: number | null, benchmark: number | null): number | null {
  if (actual == null || benchmark == null) {
    return null;
  }
  return roundMt(actual - benchmark);
}

function deriveBenchmarkFromVariance(
  actual: number | null,
  variancePct: number | null,
): number | null {
  if (actual == null || variancePct == null || variancePct === -100) {
    return null;
  }
  return roundMt(actual / (1 + variancePct / 100));
}

function parseBenchmarkFromPayload(
  payload?: Record<string, unknown>,
): Partial<BenchmarkComparisonView> {
  if (!payload) {
    return {};
  }

  const nested =
    payload['benchmark_comparison'] && typeof payload['benchmark_comparison'] === 'object'
      ? (payload['benchmark_comparison'] as Record<string, unknown>)
      : payload['benchmark'] && typeof payload['benchmark'] === 'object'
        ? (payload['benchmark'] as Record<string, unknown>)
        : null;

  const source = { ...payload, ...(nested ?? {}) };

  return {
    actualMt:
      toNum(source['actual_consumption_mt']) ??
      toNum(source['actual_mt']) ??
      toNum(source['actual_consumption']),
    tcpMt:
      toNum(source['tcp_benchmark_mt']) ??
      toNum(source['tcp_consumption_mt']) ??
      toNum(source['tcp_mt']) ??
      toNum(source['tcp_benchmark']),
    baselineMt:
      toNum(source['technical_baseline_mt']) ??
      toNum(source['baseline_mt']) ??
      toNum(source['technical_baseline']) ??
      toNum(source['baseline_benchmark']),
    tcpVariancePct:
      toNum(source['tcp_variance_pct']) ?? toNum(source['variance_tcp_pct']),
    baselineVariancePct:
      toNum(source['baseline_variance_pct']) ??
      toNum(source['variance_baseline_pct']),
    tcpVarianceMt: toNum(source['tcp_variance_mt']),
    baselineVarianceMt: toNum(source['baseline_variance_mt']),
  };
}

function parseBenchmarkFromText(text: string): Partial<BenchmarkComparisonView> {
  const source = text || '';
  const phaseTotals = parsePhaseTotals(source);

  const actualMt =
    phaseTotals.actualMt ??
    parseMtFromPatterns(source, [
      /total\s+(?:actual\s+)?consumption(?:\s*\(mt\))?[^0-9]{0,25}(\d+(?:\.\d+)?)/i,
      /actual\s+(?:total\s+)?consumption(?:\s*\(mt\))?[^0-9]{0,25}(\d+(?:\.\d+)?)/i,
      /actual\s+consumption(?:\s*\(mt\))?[^0-9]{0,20}(\d+(?:\.\d+)?)/i,
      /total\s+consumption(?:\s*\(mt\))?[^0-9]{0,20}(\d+(?:\.\d+)?)/i,
    ]);

  const tcpMt =
    phaseTotals.tcpMt ??
    parseMtFromPatterns(source, [
      /tcp\s+(?:consumption\s+)?benchmark(?:\s*\(mt\))?[^0-9]{0,25}(\d+(?:\.\d+)?)/i,
      /tcp\s+consumption[^0-9]{0,20}(\d+(?:\.\d+)?)/i,
      /(?:cp|charter[\s-]?party)\s+(?:warranted\s+)?consumption[^0-9]{0,25}(\d+(?:\.\d+)?)/i,
    ]);

  const baselineMt =
    phaseTotals.baselineMt ??
    parseMtFromPatterns(source, [
      /technical\s+baseline(?:\s+consumption)?(?:\s*\(mt\))?[^0-9]{0,25}(\d+(?:\.\d+)?)/i,
      /tech(?:nical)?\s+baseline\s+consumption[^0-9]{0,20}(\d+(?:\.\d+)?)/i,
      /baseline\s+(?:consumption\s+)?benchmark[^0-9]{0,25}(\d+(?:\.\d+)?)/i,
      /design\s+consumption[^0-9]{0,20}(\d+(?:\.\d+)?)/i,
    ]);

  const tcpVariancePct =
    calcVariancePct(actualMt, tcpMt) ??
    parseVarianceDirection(source, 'tcp') ??
    parseSignedPct(source, [
      /(?:vs|against|variance\s+(?:to|vs)?)\s*tcp[^.\n]{0,60}?([+-]?\d+(?:\.\d+)?)\s*%/i,
      /tcp\s+variance[^.\n]{0,40}?([+-]?\d+(?:\.\d+)?)\s*%/i,
    ]);

  const baselineVariancePct =
    calcVariancePct(actualMt, baselineMt) ??
    parseVarianceDirection(source, 'baseline') ??
    parseSignedPct(source, [
      /(?:vs|against|variance\s+(?:to|vs)?)\s*(?:technical\s+)?baseline[^.\n]{0,60}?([+-]?\d+(?:\.\d+)?)\s*%/i,
      /baseline\s+variance[^.\n]{0,40}?([+-]?\d+(?:\.\d+)?)\s*%/i,
    ]);

  return {
    actualMt,
    tcpMt,
    baselineMt,
    tcpVariancePct,
    baselineVariancePct,
    tcpVarianceMt: calcVarianceMt(actualMt, tcpMt),
    baselineVarianceMt: calcVarianceMt(actualMt, baselineMt),
  };
}

function mergeBenchmark(
  fromPayload: Partial<BenchmarkComparisonView>,
  fromText: Partial<BenchmarkComparisonView>,
): BenchmarkComparisonView {
  let actualMt = fromPayload.actualMt ?? fromText.actualMt ?? null;
  let tcpMt = fromPayload.tcpMt ?? fromText.tcpMt ?? null;
  let baselineMt = fromPayload.baselineMt ?? fromText.baselineMt ?? null;

  let tcpVariancePct =
    fromPayload.tcpVariancePct ??
    fromText.tcpVariancePct ??
    calcVariancePct(actualMt, tcpMt);
  let baselineVariancePct =
    fromPayload.baselineVariancePct ??
    fromText.baselineVariancePct ??
    calcVariancePct(actualMt, baselineMt);

  if (tcpMt == null && actualMt != null && tcpVariancePct != null) {
    tcpMt = deriveBenchmarkFromVariance(actualMt, tcpVariancePct);
  }
  if (baselineMt == null && actualMt != null && baselineVariancePct != null) {
    baselineMt = deriveBenchmarkFromVariance(actualMt, baselineVariancePct);
  }
  if (actualMt == null && tcpMt != null && tcpVariancePct != null) {
    actualMt = roundMt(tcpMt * (1 + tcpVariancePct / 100));
  }

  if (tcpVariancePct == null) {
    tcpVariancePct = calcVariancePct(actualMt, tcpMt);
  }
  if (baselineVariancePct == null) {
    baselineVariancePct = calcVariancePct(actualMt, baselineMt);
  }

  const tcpVarianceMt =
    fromPayload.tcpVarianceMt ??
    fromText.tcpVarianceMt ??
    calcVarianceMt(actualMt, tcpMt);
  const baselineVarianceMt =
    fromPayload.baselineVarianceMt ??
    fromText.baselineVarianceMt ??
    calcVarianceMt(actualMt, baselineMt);

  const hasData =
    actualMt != null ||
    tcpMt != null ||
    baselineMt != null ||
    tcpVariancePct != null ||
    baselineVariancePct != null;

  return {
    actualMt,
    tcpMt,
    baselineMt,
    tcpVarianceMt,
    tcpVariancePct,
    baselineVarianceMt,
    baselineVariancePct,
    hasData,
  };
}

export function parseBenchmarkComparison(
  text: string,
  payload?: Record<string, unknown>,
): BenchmarkComparisonView {
  return mergeBenchmark(parseBenchmarkFromPayload(payload), parseBenchmarkFromText(text));
}

export type ScoreClassification = 'Excellent' | 'Good' | 'Fair' | 'Poor';

export interface ScoreBreakdownResult {
  items: ScoreBreakdownItem[];
  finalScore: number;
  scoreReason: string[];
  classification: ScoreClassification;
}

function variancePenalty(pct: number): number {
  if (pct <= 0) {
    return 0;
  }
  if (pct <= 5) {
    return -Math.round(pct * 0.9);
  }
  if (pct <= 15) {
    return -Math.round(4 + (pct - 5) * 1.1);
  }
  if (pct <= 30) {
    return -Math.round(15 + (pct - 15) * 0.9);
  }
  if (pct <= 50) {
    return -Math.round(28 + (pct - 30) * 0.75);
  }
  if (pct <= 100) {
    return -Math.round(43 + (pct - 50) * 0.4);
  }
  return -Math.min(58, Math.round(63 + (pct - 100) * 0.12));
}

function varianceBonus(pct: number): number {
  if (pct >= 0) {
    return 0;
  }
  return Math.min(14, Math.round(-pct * 0.32));
}

function scoreImpactFromVariancePct(variancePct: number | null): number {
  if (variancePct == null) {
    return 0;
  }
  if (variancePct > 0) {
    return variancePenalty(variancePct);
  }
  return varianceBonus(variancePct);
}

function scoreImpactFromIdle(
  idleHours: number | null,
  portStayHours: number | null,
): number {
  if (idleHours == null) {
    return 0;
  }

  if (portStayHours != null && portStayHours > 0) {
    const ratio = idleHours / portStayHours;
    if (ratio >= 0.75) {
      return -22;
    }
    if (ratio >= 0.6) {
      return -16;
    }
    if (ratio >= 0.45) {
      return -10;
    }
    if (ratio >= 0.3) {
      return -5;
    }
    if (ratio <= 0.12) {
      return 5;
    }
    if (ratio <= 0.2) {
      return 3;
    }
    return 0;
  }

  if (idleHours >= 72) {
    return -14;
  }
  if (idleHours >= 48) {
    return -10;
  }
  if (idleHours >= 24) {
    return -5;
  }
  if (idleHours <= 8) {
    return 4;
  }
  return 0;
}

function scoreImpactFromTotalConsumption(
  tcpPct: number | null,
  baselinePct: number | null,
): number {
  if (tcpPct == null && baselinePct == null) {
    return 0;
  }

  const positive = [tcpPct, baselinePct].filter((v): v is number => v != null && v > 0);
  if (!positive.length) {
    return 0;
  }

  const peak = Math.max(...positive);
  const avg =
    positive.reduce((sum, value) => sum + value, 0) / positive.length;

  if (peak >= 100 || avg >= 80) {
    return -12;
  }
  if (peak >= 50 || avg >= 40) {
    return -8;
  }
  if (peak >= 25) {
    return -4;
  }
  return 0;
}

function resolveScoreCap(
  tcpPct: number | null,
  baselinePct: number | null,
  idleRatio: number | null,
): number {
  let cap = 100;

  if ((tcpPct != null && tcpPct > 100) || (baselinePct != null && baselinePct > 100)) {
    cap = Math.min(cap, 55);
  } else if ((tcpPct != null && tcpPct > 50) || (baselinePct != null && baselinePct > 50)) {
    cap = Math.min(cap, 65);
  } else if ((tcpPct != null && tcpPct > 30) || (baselinePct != null && baselinePct > 30)) {
    cap = Math.min(cap, 70);
  }

  if (idleRatio != null && idleRatio >= 0.7) {
    cap = Math.min(cap, 68);
  }

  if (
    tcpPct != null &&
    baselinePct != null &&
    tcpPct > 40 &&
    baselinePct > 40
  ) {
    cap = Math.min(cap, 60);
  }

  return cap;
}

export function classifyOperationalScore(score: number): ScoreClassification {
  if (score >= 85) {
    return 'Excellent';
  }
  if (score >= 70) {
    return 'Good';
  }
  if (score >= 50) {
    return 'Fair';
  }
  return 'Poor';
}

function buildScoreReasons(
  tcpPct: number | null,
  baselinePct: number | null,
  idleHours: number | null,
  portStayHours: number | null,
  cargoPct: number | null,
  manoeuvringPct: number | null,
  totalImpact: number,
): string[] {
  const reasons: string[] = [];

  if (tcpPct != null) {
    if (tcpPct > 30) {
      reasons.push('TCP consumption significantly exceeded benchmark');
    } else if (tcpPct > 10) {
      reasons.push('TCP variance negatively impacted operational performance');
    } else if (tcpPct < -5) {
      reasons.push('Consumption performed below TCP benchmark');
    }
  }

  if (baselinePct != null) {
    if (baselinePct > 30) {
      reasons.push('Technical baseline variance negatively impacted performance');
    } else if (baselinePct > 10) {
      reasons.push('Technical baseline consumption exceeded design expectation');
    } else if (baselinePct < -5) {
      reasons.push('Performance remained below technical baseline consumption');
    }
  }

  if (idleHours != null && portStayHours != null && portStayHours > 0) {
    const ratio = idleHours / portStayHours;
    if (ratio >= 0.55) {
      reasons.push('Idle duration represented majority of port stay');
    } else if (ratio >= 0.35) {
      reasons.push('Extended idle period increased total port consumption');
    } else if (ratio <= 0.15) {
      reasons.push('Low idle duration supported efficient port operations');
    }
  }

  if (cargoPct != null) {
    if (cargoPct > 10) {
      reasons.push('Cargo operations consumed above benchmark levels');
    } else if (cargoPct <= 5 && cargoPct < 0) {
      reasons.push('Cargo operations remained efficient against benchmark');
    }
  }

  if (manoeuvringPct != null) {
    if (manoeuvringPct > 10) {
      reasons.push('Manoeuvring consumption exceeded expected efficiency');
    } else if (manoeuvringPct < -5) {
      reasons.push('Manoeuvring performance remained efficient');
    }
  }

  if (totalImpact < 0) {
    reasons.push('Combined port consumption exceeded benchmark expectations');
  }

  if (!reasons.length) {
    reasons.push('Score derived from report benchmark and operational efficiency signals');
  }

  return [...new Set(reasons)].slice(0, 5);
}

export function deriveScoreBreakdown(
  signals: VesselInsightSignals,
  benchmark: BenchmarkComparisonView,
): ScoreBreakdownResult {
  let score = 100;
  const items: ScoreBreakdownItem[] = [];

  const tcpPct = signals.tcpVariancePct ?? benchmark.tcpVariancePct;
  const tcpImpact = scoreImpactFromVariancePct(tcpPct);
  score += tcpImpact;
  items.push({ label: 'TCP Variance Impact', impact: tcpImpact });

  const baselinePct = signals.baselineVariancePct ?? benchmark.baselineVariancePct;
  const baselineImpact = scoreImpactFromVariancePct(baselinePct);
  score += baselineImpact;
  items.push({ label: 'Technical Baseline Impact', impact: baselineImpact });

  const idleImpact = scoreImpactFromIdle(signals.idleHours, signals.portStayHours);
  score += idleImpact;
  items.push({ label: 'Idle Duration Impact', impact: idleImpact });

  const cargoImpact = scoreImpactFromVariancePct(signals.cargoVariancePct);
  score += cargoImpact;
  items.push({ label: 'Cargo Operations Impact', impact: cargoImpact });

  const manoeuvringImpact = scoreImpactFromVariancePct(signals.manoeuvringVariancePct);
  score += manoeuvringImpact;
  items.push({ label: 'Manoeuvring Impact', impact: manoeuvringImpact });

  const totalImpact = scoreImpactFromTotalConsumption(tcpPct, baselinePct);
  score += totalImpact;
  items.push({ label: 'Total Port Consumption Impact', impact: totalImpact });

  const idleRatio =
    signals.idleHours != null &&
    signals.portStayHours != null &&
    signals.portStayHours > 0
      ? signals.idleHours / signals.portStayHours
      : null;

  const rawScore = clamp(Math.round(score), 0, 100);
  const cap = resolveScoreCap(tcpPct, baselinePct, idleRatio);
  const finalScore = Math.min(rawScore, cap);

  return {
    items,
    finalScore,
    classification: classifyOperationalScore(finalScore),
    scoreReason: buildScoreReasons(
      tcpPct,
      baselinePct,
      signals.idleHours,
      signals.portStayHours,
      signals.cargoVariancePct,
      signals.manoeuvringVariancePct,
      totalImpact,
    ),
  };
}

export function deriveAiConfidence(meta: ExtractionQualityMeta): number {
  let confidence = 68;

  if (meta.pageCount >= 2) confidence += 8;
  if (meta.pagesIncluded.length >= 3) confidence += 6;
  if (meta.textChars >= 400) confidence += 8;
  if (meta.textChars >= 1200) confidence += 6;
  if (meta.markerCount >= 4) confidence += 8;
  if (meta.markerCount >= 6) confidence += 4;
  if (!meta.usedOcr) confidence += 6;
  if (meta.usedOcr) confidence -= 10;

  return Math.min(98, Math.max(62, Math.round(confidence)));
}

export function countBenchmarkMarkers(text: string): number {
  const markers = [/idle/i, /cargo/i, /manoeuvr|maneuver/i, /tcp/i, /baseline/i, /consumption/i, /benchmark/i];
  return markers.filter((rx) => rx.test(text)).length;
}

export function deriveInsightTags(
  signals: VesselInsightSignals,
  benchmark: BenchmarkComparisonView,
  operation = '',
): InsightTagView[] {
  const tags: InsightTagView[] = [];
  const tcpPct = signals.tcpVariancePct ?? benchmark.tcpVariancePct;

  if (tcpPct != null) {
    if (tcpPct > 5) tags.push({ label: 'Above Benchmark', variant: 'orange' });
    if (tcpPct < -5) tags.push({ label: 'Fuel Saving', variant: 'blue' });
    if (tcpPct < -3) tags.push({ label: 'Below Benchmark', variant: 'yellow' });
  }

  if (signals.portStayHours != null && signals.portStayHours >= 72) {
    tags.push({ label: 'High Duration', variant: 'orange' });
  } else if (signals.idleHours != null && signals.idleHours >= 24) {
    tags.push({ label: 'Excess Idle Consumption', variant: 'orange' });
  }

  if (signals.cargoVariancePct != null && signals.cargoVariancePct <= 5) {
    tags.push({ label: 'Efficient Cargo Operations', variant: 'blue' });
  }

  if (tcpPct != null && tcpPct > 10) {
    tags.push({ label: 'Review Required', variant: 'yellow' });
  }

  if (lowerIncludesDelay(signals)) {
    tags.push({ label: 'Operational Delay', variant: 'purple' });
  }

  if (operation && operation !== '—') {
    tags.push({
      label: `${operation} Operation`.replace(' Operation Operation', ' Operation'),
      variant: 'purple',
    });
  }

  const unique = new Map<string, InsightTagView>();
  tags.forEach((tag) => unique.set(tag.label, tag));
  return Array.from(unique.values()).slice(0, 5);
}

function lowerIncludesDelay(signals: VesselInsightSignals): boolean {
  return (
    (signals.idleHours != null && signals.idleHours >= 36) ||
    (signals.portStayHours != null && signals.portStayHours >= 84)
  );
}

export function formatVarianceLabel(
  pct: number | null,
  mt: number | null,
): string {
  if (pct == null && mt == null) {
    return 'N/A';
  }
  const mtLabel = mt == null ? '' : ` ${mt > 0 ? '+' : ''}${mt} MT`;
  if (pct != null) {
    return `${pct > 0 ? '+' : ''}${pct}%${mtLabel}`.trim();
  }
  return mtLabel.trim() || 'N/A';
}

export function benchmarkBarWidth(value: number | null, max: number): number {
  if (value == null || max <= 0) {
    return 0;
  }
  return Math.min(100, Math.max(8, Math.round((value / max) * 100)));
}
